import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from './store';
import { getCurrentUser } from './store/slices/authSlice';
import { PermissionGuard } from './components/auth/PermissionGuard';
import { useUserScopes } from './hooks/useUserScopes';
import { Permission } from './utils/permissions';

// Components
import Layout from './components/Layout';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PlanningPeriodsPage from './pages/planning/PlanningPeriodsPage';
import InvestibleStructuresPage from './pages/structures/InvestibleStructuresPage';
import DivisionsPage from './pages/divisions/DivisionsPage';
import ProgramsPage from './pages/programs/ProgramsPage';
import TeamsPage from './pages/teams/TeamsPage';
import RateCardsPage from './pages/rateCards/RateCardsPage';
import ResourceLevelsPage from './pages/resourceLevels/ResourceLevelsPage';
import ResourcesPage from './pages/resources/ResourcesPage';
import ResourceTeamAssignmentsPage from './pages/resourceAssignments/ResourceTeamAssignmentsPage';
import ResourceIntakeRequestsPage from './pages/resourceIntakeRequests/ResourceIntakeRequestsPage';
import EpicsPage from './pages/epics/EpicsPage';
import FeaturesPage from './pages/features/FeaturesPage';
import OKRsPage from './pages/okrs/OKRsPage';
import ConsolidatedAllocationPage from './pages/allocations/ConsolidatedAllocationPage';
import ScenariosPage from './pages/scenarios/ScenariosPage';
import ExecutiveDashboardPage from './pages/dashboard/ExecutiveDashboardPage';
import ExecutiveDashboardV2Page from './pages/dashboard/ExecutiveDashboardV2Page';
import ISOwnerDashboardPage from './pages/dashboard/ISOwnerDashboardPage';
import ProgramLeadDashboardPage from './pages/dashboard/ProgramLeadDashboardPage';
import AdminDashboardPage from './pages/dashboard/AdminDashboardPage';
import DependenciesPage from './pages/dependencies/DependenciesPage';
import { UsersPage } from './pages/users/UsersPage';
import { UserProfilePage } from './pages/users/UserProfilePage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import NotificationPreferencesPage from './pages/notifications/NotificationPreferencesPage';
import ProgramCapacityPage from './pages/capacity/ProgramCapacityPage';
import FinanceOperationalCostPage from './pages/finance/FinanceOperationalCostPage';
import DivisionBudgetManagementPage from './pages/finance/budget/DivisionBudgetManagementPage';
import CostDashboardPage from './pages/costs/CostDashboardPage';
import TimelinePage from './pages/timeline/TimelinePage';
import SearchResultsPage from './pages/search/SearchResultsPage';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token, isLoading } = useSelector((state: RootState) => state.auth);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Teams Guard Component
// Blocks IS_OWNER from accessing Teams (they can only see Programs)
const TeamsGuard: React.FC<{ permission: Permission; children: React.ReactNode }> = ({ permission, children }) => {
  const { canAccessTeams } = useUserScopes();

  if (!canAccessTeams()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PermissionGuard permission={permission}>
      {children}
    </PermissionGuard>
  );
};

// Resource Management Guard Component
// Blocks IS_OWNER and users without resource management scope
const ResourceGuard: React.FC<{ permission: Permission; children: React.ReactNode }> = ({ permission, children }) => {
  const { canAccessResourceManagement } = useUserScopes();

  if (!canAccessResourceManagement()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PermissionGuard permission={permission}>
      {children}
    </PermissionGuard>
  );
};

// Scenario Guard Component
// Blocks IS_OWNER from accessing Scenarios
const ScenarioGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canAccessScenarios } = useUserScopes();

  if (!canAccessScenarios()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, isLoading } = useSelector((state: RootState) => state.auth);

  // Check for existing token on app load
  useEffect(() => {
    if (token) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, token]);

  return (
    <div className="min-h-screen transition-colors">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  
                  {/* Planning Routes */}
                  <Route 
                    path="/planning-periods" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_PLANNING_PERIODS}>
                        <PlanningPeriodsPage />
                      </PermissionGuard>
                    } 
                  />
                  <Route
                    path="/scenarios"
                    element={
                      <PermissionGuard permission={Permission.VIEW_SCENARIOS}>
                        <ScenarioGuard>
                          <ScenariosPage />
                        </ScenarioGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route 
                    path="/dependencies" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_DEPENDENCIES}>
                        <DependenciesPage />
                      </PermissionGuard>
                    } 
                  />
                  
                  {/* Portfolio Routes */}
                  <Route 
                    path="/investible-structures" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_INVESTIBLE_STRUCTURES}>
                        <InvestibleStructuresPage />
                      </PermissionGuard>
                    } 
                  />
                  <Route
                    path="/epics"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EPICS}>
                        <EpicsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/features" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_FEATURES}>
                        <FeaturesPage />
                      </PermissionGuard>
                    } 
                  />
                  <Route 
                    path="/okrs" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_EPICS}>
                        <OKRsPage />
                      </PermissionGuard>
                    } 
                  />
                  
                  {/* Delivery Routes */}
                  <Route
                    path="/divisions"
                    element={
                      <PermissionGuard permission={Permission.VIEW_PROGRAMS}>
                        <DivisionsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/programs"
                    element={
                      <PermissionGuard permission={Permission.VIEW_PROGRAMS}>
                        <ProgramsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/teams"
                    element={
                      <TeamsGuard permission={Permission.VIEW_TEAMS}>
                        <TeamsPage />
                      </TeamsGuard>
                    }
                  />
                  <Route
                    path="/rate-cards"
                    element={
                      <ResourceGuard permission={Permission.VIEW_PROGRAMS}>
                        <RateCardsPage />
                      </ResourceGuard>
                    }
                  />
                  <Route
                    path="/resource-levels"
                    element={
                      <ResourceGuard permission={Permission.VIEW_PROGRAMS}>
                        <ResourceLevelsPage />
                      </ResourceGuard>
                    }
                  />
                  <Route
                    path="/resources"
                    element={
                      <ResourceGuard permission={Permission.VIEW_PROGRAMS}>
                        <ResourcesPage />
                      </ResourceGuard>
                    }
                  />
                  <Route
                    path="/resource-assignments"
                    element={
                      <ResourceGuard permission={Permission.VIEW_PROGRAMS}>
                        <ResourceTeamAssignmentsPage />
                      </ResourceGuard>
                    }
                  />
                  <Route
                    path="/resource-intake-requests"
                    element={
                      <ResourceGuard permission={Permission.VIEW_PROGRAMS}>
                        <ResourceIntakeRequestsPage />
                      </ResourceGuard>
                    }
                  />
                  <Route
                    path="/capacity/programs"
                    element={
                      <PermissionGuard permission={Permission.VIEW_CAPACITY}>
                        <ProgramCapacityPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/finance/operational-cost"
                    element={
                      <PermissionGuard permission={Permission.VIEW_CAPACITY}>
                        <FinanceOperationalCostPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/finance/division-budgets"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EXECUTIVE_DASHBOARD}>
                        <DivisionBudgetManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/costs/dashboard"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EXECUTIVE_DASHBOARD}>
                        <CostDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/timeline"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EPICS}>
                        <TimelinePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/allocations/consolidated"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EPICS}>
                        <ConsolidatedAllocationPage />
                      </PermissionGuard>
                    }
                  />

                  {/* Admin Routes */}
                  <Route 
                    path="/users" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_USERS}>
                        <UsersPage />
                      </PermissionGuard>
                    } 
                  />
                  
                  {/* Profile (always accessible) */}
                  <Route path="/profile" element={<UserProfilePage />} />
                  
                  {/* Notification Routes (always accessible) */}
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/notifications/preferences" element={<NotificationPreferencesPage />} />

                  {/* Search (always accessible) */}
                  <Route path="/search" element={<SearchResultsPage />} />

                  {/* Dashboard Routes */}
                  <Route
                    path="/reports/admin"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EXECUTIVE_DASHBOARD}>
                        <AdminDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/reports/executive"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EXECUTIVE_DASHBOARD}>
                        <ExecutiveDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/reports/executive-v2"
                    element={
                      <PermissionGuard permission={Permission.VIEW_EXECUTIVE_DASHBOARD}>
                        <ExecutiveDashboardV2Page />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/reports/is-owner" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_IS_OWNER_DASHBOARD}>
                        <ISOwnerDashboardPage />
                      </PermissionGuard>
                    } 
                  />
                  <Route 
                    path="/reports/program-lead" 
                    element={
                      <PermissionGuard permission={Permission.VIEW_PROGRAM_LEAD_DASHBOARD}>
                        <ProgramLeadDashboardPage />
                      </PermissionGuard>
                    } 
                  />
                  
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

export default App;